// Paalm · Panier : le panier lateral (balise <cart-drawer>, comme le panier d'origine du theme).
// L'affichage vient de la section (Liquid). Ce script appelle le panier Shopify, puis remplace le contenu
// par la nouvelle version de la section, renvoyee dans la meme reponse (Section Rendering API).
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

const racine = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

class PaalmPanier extends HTMLElement {
  connectedCallback() {
    this.codeOuvert = false;
    this.occupe = false;
    this.onOpen = () => this.open();
    this.onClose = () => this.close();
    this.onUpdated = (e) => this.#majDepuisEvenement(e);
    this.onKey = (e) => { if (e.key === 'Escape' && this.isOpen()) this.close(); };
    this.addEventListener('click', (e) => this.#clic(e));
    this.addEventListener('submit', (e) => this.#soumission(e));
    document.addEventListener(ThemeEvents.cartOpen, this.onOpen);
    document.addEventListener(ThemeEvents.cartClose, this.onClose);
    document.addEventListener(ThemeEvents.cartUpdated, this.onUpdated);
    document.addEventListener('keydown', this.onKey);
    if (window.Shopify && Shopify.designMode) {
      this.onSelect = (e) => { if (this.#estMaSection(e)) this.open(); };
      this.onDeselect = (e) => { if (this.#estMaSection(e)) this.close(); };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      document.addEventListener('shopify:block:select', this.onSelect);
    }
    this.#synchroCadeaux();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartOpen, this.onOpen);
    document.removeEventListener(ThemeEvents.cartClose, this.onClose);
    document.removeEventListener(ThemeEvents.cartUpdated, this.onUpdated);
    document.removeEventListener('keydown', this.onKey);
    if (this.onSelect) {
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.removeEventListener('shopify:block:select', this.onSelect);
    }
  }

  #estMaSection(e) {
    return e.detail && this.dataset.sectionId && this.dataset.sectionId.endsWith(e.detail.sectionId);
  }

  // Meme interface que le panier d'origine : open(), close(), isOpen().
  open() {
    if (this.isOpen()) return;
    this.dernierFocus = document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.querySelector('[data-pp-fermer]')?.focus({ preventScroll: true }));
  }

  close() {
    if (!this.isOpen()) return;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (this.dernierFocus && this.dernierFocus.focus) this.dernierFocus.focus({ preventScroll: true });
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  // ---------- rendu ----------
  #majDepuisEvenement(e) {
    if (e.detail?.data?.source === 'paalm-panier') return;
    const html = e.detail?.data?.sections?.[this.dataset.sectionId];
    if (html) this.#rendre(html);
    else this.#recharger();
  }

  async #recharger() {
    const r = await fetch(`${location.pathname}?sections=${encodeURIComponent(this.dataset.sectionId)}`, { headers: { Accept: 'application/json' } });
    if (!r.ok) return;
    const data = await r.json();
    if (data[this.dataset.sectionId]) this.#rendre(data[this.dataset.sectionId]);
  }

  #rendre(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const neuf = doc.querySelector('cart-drawer');
    if (!neuf) return;
    const defile = this.querySelector('.pp-defile');
    const position = defile ? defile.scrollTop : 0;
    this.innerHTML = neuf.innerHTML;
    this.dataset.nb = neuf.dataset.nb;
    this.classList.toggle('pp--vide', neuf.classList.contains('pp--vide'));
    const d2 = this.querySelector('.pp-defile');
    if (d2) d2.scrollTop = position;
    if (this.codeOuvert) this.#ouvrirCode(false);
    this.#synchroCadeaux();
  }

  // ---------- appels au panier ----------
  async #appel(chemin, corps, source) {
    if (this.occupe) return null;
    this.occupe = true;
    this.classList.add('pp--charge');
    try {
      const r = await fetch(`${racine}${chemin}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...corps, sections: this.dataset.sectionId, sections_url: location.pathname }),
      });
      const data = await r.json();
      if (!r.ok || data.status) {
        this.#erreur(data.description || data.message || '');
        return null;
      }
      const html = data.sections && data.sections[this.dataset.sectionId];
      if (html) this.#rendre(html);
      else await this.#recharger();
      // Les autres composants du theme (icone panier) se mettent a jour ; le contenu est deja a jour.
      document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: Number(this.dataset.nb || 0), source: 'paalm-panier', sections: data.sections }));
      return data;
    } catch (err) {
      console.error(err);
      this.#erreur('');
      return null;
    } finally {
      this.occupe = false;
      this.classList.remove('pp--charge');
      if (!this.synchro) this.#synchroCadeaux();
    }
  }

  #erreur(message) {
    const p = this.querySelector('[data-pp-code-err]');
    if (p && message) {
      p.textContent = message;
      p.hidden = false;
    } else {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message || 'Une erreur est survenue, réessaie.' } }));
    }
  }

  #codes() {
    return (this.querySelector('[data-pp-codes]')?.dataset.ppCodes || '').split(',').filter(Boolean);
  }

  async #appliquerCode(code) {
    const err = this.querySelector('[data-pp-code-err]');
    if (!code) {
      if (err) { err.textContent = this.dataset.errVide; err.hidden = false; }
      return;
    }
    const avant = this.#codes();
    if (avant.some((c) => c.toLowerCase() === code.toLowerCase())) { this.codeOuvert = false; this.#ouvrirCode(false); return; }
    await this.#appel('cart/update.js', { discount: [...avant, code].join(',') });
    // Shopify garde un code refuse dans le panier : on verifie, on le retire et on l'explique.
    const panier = await (await fetch(`${racine}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const essai = (panier.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
    if (!essai || !essai.applicable) {
      const gardes = (panier.discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
      await this.#appel('cart/update.js', { discount: gardes.join(',') });
      this.codeOuvert = true;
      this.#ouvrirCode(false);
      const e = this.querySelector('[data-pp-code-err]');
      if (e) { e.textContent = (this.dataset.errCode || '').replace('[code]', code.toUpperCase()); e.hidden = false; }
    } else {
      this.codeOuvert = false;
    }
  }

  #ouvrirCode(focus) {
    const form = this.querySelector('[data-pp-code-form]');
    if (!form) return;
    form.hidden = !this.codeOuvert;
    this.querySelector('.pp-code')?.setAttribute('aria-expanded', String(this.codeOuvert));
    if (this.codeOuvert && focus) form.querySelector('input')?.focus();
  }

  // Cadeaux de palier : ajoutes quand le seuil est atteint, retires en dessous (une seule fois par rendu).
  async #synchroCadeaux() {
    const bloc = this.querySelector('[data-pp-cadeaux]');
    if (!bloc || this.occupe || this.synchro) return;
    let cadeaux = [];
    try { cadeaux = JSON.parse(bloc.textContent || '[]'); } catch (e) { return; }
    const a_faire = cadeaux.find((c) => (c.atteint && !c.ligne) || (!c.atteint && c.ligne));
    if (!a_faire) return;
    this.synchro = true;
    try {
      if (a_faire.atteint) await this.#appel('cart/add.js', { items: [{ id: a_faire.variante, quantity: 1, properties: { _paalm_cadeau: '1' } }] });
      else await this.#appel('cart/change.js', { id: a_faire.ligne, quantity: 0 });
    } finally {
      this.synchro = false;
    }
  }

  // ---------- interactions ----------
  #clic(e) {
    if (e.target === this) { this.close(); return; }
    const b = e.target.closest('button, a');
    if (!b || !this.contains(b)) return;
    if (b.hasAttribute('data-pp-fermer')) { this.close(); return; }
    if (b.hasAttribute('data-pp-qte')) {
      const ligne = b.closest('[data-cle]');
      this.#appel('cart/change.js', { id: ligne.dataset.cle, quantity: Number(b.dataset.ppQte) });
      return;
    }
    if (b.hasAttribute('data-pp-ajout')) {
      this.#appel('cart/add.js', { items: [{ id: Number(b.dataset.ppAjout), quantity: 1 }] });
      return;
    }
    if (b.hasAttribute('data-pp-code-ouvrir')) {
      this.codeOuvert = !this.codeOuvert;
      const err = this.querySelector('[data-pp-code-err]');
      if (err) err.hidden = true;
      this.#ouvrirCode(true);
      return;
    }
    if (b.hasAttribute('data-pp-code-retirer')) {
      const garder = this.#codes().filter((c) => c !== b.dataset.ppCodeRetirer);
      this.#appel('cart/update.js', { discount: garder.join(',') });
    }
  }

  #soumission(e) {
    const form = e.target.closest('[data-pp-code-form]');
    if (!form) return; // le formulaire « Commander » part normalement vers le paiement
    e.preventDefault();
    this.#appliquerCode((form.querySelector('input')?.value || '').trim());
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', PaalmPanier);
}
